import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import ServiceDetails from '../../../components/Appointment/ServiceDetails';
import CustomerChangeModal from '../../../components/Appointment/CustomerChangeModal';
import CustomerSearchModal from '../../../components/Appointment/CustomerSearchModal';
import AddCustomerModal from '../../../components/Appointment/AddCustomerModal';
import VehicleListModal from '../../../components/Appointment/VehicleListModal';
import CampaignValidationModal from '../../../components/Appointment/CampaignValidationModal';
import { getData, postData, putData } from '../../../utils/httpClient';
import authService from 'services/authService';
import {
  findMaintenanceServiceType,
  getNextBusinessDateInput,
  isMaintenanceServiceType,
} from '../../../utils/defaultMaintenanceService';
import ConfirmModal from '../../../components/modal/ConfirmModal';
import {
  DEFAULT_WARRANTY_TYPE_CODE,
  getWarrantyTypeLabel,
  isWarrantyServiceType,
} from '../../../utils/warrantyTypes';
import { sumServiceEventsMinutes } from '../../../utils/serviceDuration';
import { buildServicesQueryParams } from '../../../utils/filterServicesForVehicle';
import {
  buildAgendaQuery,
  getAgendaViewRange,
} from '../../../utils/agendaDateRange';

const parseAppointmentDateTime = (date, time) => {
  if (!date || !time) return null;
  const [year, month, day] = String(date)
    .slice(0, 10)
    .split('-')
    .map(Number);
  const [hours, minutes, seconds = 0] = String(time)
    .slice(0, 8)
    .split(':')
    .map(Number);
  const result = new Date(year, month - 1, day, hours, minutes, seconds);
  return Number.isNaN(result.getTime()) ? null : result;
};

const ServiceAppointmentForm = () => {
  const navigate = useNavigate();
  const [isSearchingVehicles, setIsSearchingVehicles] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState({ searchData: '' });
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [showByDate, setShowByDate] = useState(true);
  const [vehiclesList, setVehiclesList] = useState([]);
  const [matchedCustomers, setMatchedCustomers] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [customerTypesList, setCustomerTypesList] = useState([]);
  const [localitiesList, setLocalitiesList] = useState([]);
  const [mechanicsList, setMechanicsList] = useState([]);
  const [servicesTypeList, setServicesTypeList] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [customerTransferList, setCustomerTransferList] = useState([]);
  const [appointmentList, setAppointmentList] = useState([]);
  const [holidayList, setHolidayList] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newService, setNewService] = useState({
    mechanicId: '',
    serviceTypeId: '',
    serviceId: '',
    estimatedTime: 0,
    observations: '',
    date: '',
    time: '',
    customerTransferId: '',
    parking: '1',
    warrantyType: DEFAULT_WARRANTY_TYPE_CODE,
    volkswagenCost: '',
  });
  const [showVehicleListModal, setShowVehicleListModal] = useState(false);
  const [showChangeCustomerModal, setShowChangeCustomerModal] = useState(false);
  const [showSearchCustomerModal, setShowSearchCustomerModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [vehicleCampaigns, setVehicleCampaigns] = useState([]);
  const [overlapConfirm, setOverlapConfirm] = useState({
    isOpen: false,
    message: '',
    allowOverlap: false,
  });
  const [noMechanicConfirm, setNoMechanicConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resolveMechanicId = (raw) => {
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  };
  const toast = useToast();

  const searchVehicle = async () => {
    const term = vehicleInfo.searchData.trim();
    if (!term) return;
    setIsSearchingVehicles(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '10',
        data: term,
      });
      const [vehicleResponse, customerResponse] = await Promise.all([
        getData(`/vehicles?${params.toString()}`),
        getData(`/customers?${params.toString()}`),
      ]);
      const vehicles = vehicleResponse?.payload?.vehicles || [];
      const customers = customerResponse?.payload?.customers || [];
      if (vehicles.length > 0 || customers.length > 0) {
        setVehiclesList(vehicles);
        setMatchedCustomers(vehicles.length > 0 ? [] : customers);
        setShowVehicleListModal(true);
      } else {
        setVehiclesList([]);
        setMatchedCustomers([]);
        toast({
          title: 'Sin resultados',
          description: 'No se encontró ningún vehículo ni cliente con ese dato.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'No se encontraron vehículos.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSearchingVehicles(false);
    }
  };

  const toAgendaUpdatePayload = (appointment) => {
    const {
      customer,
      vehicle,
      service,
      mechanic,
      serviceType,
      customerTransfer,
      vehicleService,
      allowOverlap,
      ...agendaFields
    } = appointment;
    return agendaFields;
  };

  const postNewService = async (appointment) => {
    try {
      const serviceId = appointment.service?.id ?? appointment.serviceId ?? null;
      const payload = {
        customerId: appointment.customerId,
        vehicleId: appointment.vehicleId,
        mechanicId: appointment.mechanicId,
        invoiceId: null,
        creatorUserId: authService.getCurrentUser()?.id || null,
        description: appointment.description,
        observations: null,
        customerObservations: appointment.observations,
        mechanicObservation: null,
        customerTransferId: appointment.customerTransferId,
        canceledObservation: null,
        date: appointment.date,
        time: appointment.time,
        statusId: 1,
        serviceTypeId: appointment.serviceTypeId,
        createdAt: new Date(),
        updatedAt: new Date(),
        serviceName: appointment.description,
        volkswagenCost: (() => {
          const raw =
            appointment.volkswagenCost ??
            appointment.service?.volkswagenCost ??
            null;
          if (raw === '' || raw == null) return null;
          const n = Number(String(raw).replace(',', '.'));
          return Number.isFinite(n) ? n : null;
        })(),
        warrantyType: appointment.warrantyType || null,
        totalTime: Number(appointment.estimatedTime) || 0,
        serviceStart: null,
        serviceEnd: null,
        serviceId,
        vehicleServiceEventsHistories:
          appointment.service?.servicesEvents?.map((serviceEvent) => ({
            serviceId,
            eventId: serviceEvent.eventId,
            eventName: serviceEvent.event?.name || serviceEvent.eventName,
          })) || [],
      };
      const response = await postData('/vehicleServices', payload);
      const vehicleServiceId = response?.payload?.vehicleService?.id;
      if (!vehicleServiceId) {
        throw new Error('No se pudo crear el servicio del vehículo.');
      }
      await putAppointment({
        ...toAgendaUpdatePayload(appointment),
        id: appointment.id,
        vehicleServiceId,
      });
      return response.payload.vehicleService;
    } catch (error) {
      throw new Error(
        error?.response?.data?.message ||
          error?.message ||
          'No se pudo crear el servicio.',
      );
    }
  };

  const putAppointment = async (appointment) => {
    if (!appointment?.id) {
      throw new Error('No se encontró la cita a actualizar.');
    }
    try {
      await putData(
        `/agenda/${appointment.id}`,
        toAgendaUpdatePayload(appointment),
      );
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'No se pudo actualizar la agenda.';
      throw new Error(message);
    }
  };

  const getMechanics = async () => {
    try {
      const response = await getData('/users/mechanics');
      setMechanicsList(response?.payload?.mechanics || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los operarios.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getServicesType = async () => {
    try {
      const response = await getData('/serviceTypes');
      const preferredOrder = [
        'Mantenimiento',
        'Garantia',
        'Reparación',
        'Cita urgente',
        'Recepción activa',
        'Recepción activa no aceptada',
        'Servicio express',
        'Reparación repetida',
        'Cortesia taller',
      ];
      const types = [...(response.payload.serviceTypes || [])].sort((a, b) => {
        const ai = preferredOrder.indexOf(a.name);
        const bi = preferredOrder.indexOf(b.name);
        if (ai === -1 && bi === -1) {
          return String(a.name || '').localeCompare(String(b.name || ''), 'es');
        }
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
      setServicesTypeList(types);
      return types;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los tipos de servicio.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return [];
    }
  };

  const getCustomerTransferList = async () => {
    try {
      const response = await getData('/customerTransfer');
      const transfers = response?.payload?.customerTransfers || [];
      const otherTransfer = transfers.find(
        (transfer) => transfer?.name?.trim().toLocaleLowerCase('es') === 'otro',
      );
      setCustomerTransferList(transfers);
      setNewService((prev) => ({
        ...prev,
        customerTransferId: otherTransfer ? String(otherTransfer.id) : '',
      }));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los traslados del cliente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getServices = async (serviceType, vehicle = selectedVehicle) => {
    const typeId = Number(serviceType);
    if (!typeId || Number.isNaN(typeId)) {
      setServicesList([]);
      return;
    }
    try {
      const qs = buildServicesQueryParams(typeId, vehicle);
      const response = await getData(`/services?${qs}`);
      setServicesList(response?.payload?.services || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los servicios.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getAppointments = useCallback(async (from, to) => {
    try {
      if (!from || !to) return;
      const response = await getData(`/agenda${buildAgendaQuery(from, to)}`);
      setAppointmentList(response?.payload?.agendas || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las agendas.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  const handleAppointmentRangeChange = useCallback((from, to) => {
    getAppointments(from, to);
  }, [getAppointments]);

  useEffect(() => {
    getMechanics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getHolidays = async () => {
    try {
      const response = await getData('/holidays');
      setHolidayList(response.payload.holidays);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los feriados.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getCustomerTypes = async () => {
    try {
      const response = await getData('/customers/types');
      setCustomerTypesList(response.payload.types);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los tipos de cliente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getLocalities = async () => {
    try {
      const response = await getData('/localities');
      setLocalitiesList(response.payload.localities);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las localidades.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSelectVehicle = async (vehicle) => {
    setShowVehicleListModal(false);
    setVehicleInfo({ searchData: vehicle.plate || vehicleInfo.searchData });
    setSelectedVehicle(vehicle);
    setSelectedCustomer(vehicle.currentCustomer || vehicle.customer);
    setShowByDate(true);
    setSelectedDate('');
    setNewService((prev) => ({
      ...prev,
      date: '',
      time: '',
      mechanicId: '',
      serviceTypeId: '',
      serviceId: '',
      estimatedTime: 0,
      customerTransferId: '',
      parking: '1',
      volkswagenCost: '',
      observations: prev.observations,
    }));

    const types = await getServicesType();
    await Promise.all([
      getMechanics(),
      getCustomerTransferList(),
      (() => {
        const range = getAgendaViewRange(newService.date || new Date(), 'work_week');
        return getAppointments(range.from, range.to);
      })(),
      getHolidays(),
    ]);
    const maintenanceType = findMaintenanceServiceType(types);
    if (maintenanceType?.id) {
      try {
        await getServices(maintenanceType.id, vehicle);
        setNewService((prev) => ({
          ...prev,
          serviceTypeId: String(maintenanceType.id),
          serviceId: '',
          service: null,
          description: '',
          estimatedTime: 0,
        }));
      } catch {
        /* mantenimiento opcional */
      }
    }

    validateVehicleCampaigns(vehicle.chassisNumber).catch(() => {});
  };

  const validateVehicleCampaigns = async (chassisNumber) => {
    try {
      const response = await getData(`/campaigns/vehicle/${chassisNumber}`);
      if (response.payload?.campaigns?.length > 0) {
        setVehicleCampaigns(response.payload.campaigns);
        if (response.payload.unreviewedCount > 0) {
          setShowCampaignModal(true);
        }
      }
    } catch {
      /* campañas opcionales */
    }
  };

  const handleChangeCustomer = () => {
    setShowChangeCustomerModal(true);
    setCustomerSearch('');
    setCustomersList([]);
    setSearchCompleted(false);
  };

  const confirmCustomerChange = () => {
    setShowChangeCustomerModal(false);
    setShowSearchCustomerModal(true);
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowSearchCustomerModal(false);
    setCustomerSearch('');
    setCustomersList([]);
    setShowAddCustomerModal(false);
  };

  const handleAddCustomer = async () => {
    await getCustomerTypes();
    await getLocalities();
    setShowSearchCustomerModal(false);
    setShowAddCustomerModal(true);
  };

  const handleCustomerSearch = async (event) => {
    if (event.key === 'Enter') {
      try {
        const params = new URLSearchParams({
          page: '1',
          limit: '10',
          data: customerSearch.trim(),
        });
        const response = await getData(`/customers?${params.toString()}`);
        setCustomersList(response.payload.customers);
        setSearchCompleted(true);
      } catch (error) {
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los clientes.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleToggleButtonDate = () => {
    setShowByDate((prev) => {
      const next = !prev;
      if (next) {
        setSelectedDate('');
        setNewService((s) => ({ ...s, date: '', time: '' }));
      } else {
        const nextOpenDay = getNextBusinessDateInput();
        setSelectedDate(nextOpenDay);
        setNewService((s) => ({ ...s, date: nextOpenDay, time: '' }));
      }
      return next;
    });
  };

  const handleServiceChange = (key, value) => {
    setNewService((prev) => {
      if (key === 'serviceId') {
        if (!value) {
          return {
            ...prev,
            serviceId: '',
            service: null,
            description: '',
            estimatedTime: 0,
          };
        }
        const selectedService = servicesList.find(
          (service) => service.id === parseInt(value, 10),
        );
        const totalEstimatedTime = sumServiceEventsMinutes(
          selectedService?.servicesEvents || [],
        );
        return {
          ...prev,
          serviceId: value,
          service: selectedService || null,
          description: selectedService?.name || '',
          estimatedTime: totalEstimatedTime || 30,
          volkswagenCost:
            selectedService?.volkswagenCost != null &&
            selectedService?.volkswagenCost !== ''
              ? String(selectedService.volkswagenCost)
              : prev.volkswagenCost || '',
        };
      }
      if (key === 'serviceTypeId') {
        const isMaint = isMaintenanceServiceType(value, servicesTypeList);
        const isWarranty = isWarrantyServiceType(value, servicesTypeList);
        if (isMaint) {
          getServices(value);
        } else {
          setServicesList([]);
        }
        return {
          ...prev,
          serviceTypeId: value,
          serviceId: '',
          service: null,
          description: '',
          estimatedTime: isMaint ? 0 : 30,
          warrantyType: isWarranty ? DEFAULT_WARRANTY_TYPE_CODE : null,
        };
      }
      return { ...prev, [key]: value };
    });
  };

  const handleCancelService = () => {
    setVehicleInfo({ searchData: '' });
    setCustomerSearch('');
    setSelectedDate('');
    setShowByDate(true);
    setVehiclesList([]);
    setCustomersList([]);
    setAppointmentList([]);
    setSelectedVehicle(null);
    setSelectedCustomer(null);
    setNewService({
      mechanicId: '',
      serviceTypeId: '',
      serviceId: '',
      estimatedTime: 0,
      observations: '',
      date: '',
      time: '',
      customerTransferId: '',
      parking: '1',
      warrantyType: DEFAULT_WARRANTY_TYPE_CODE,
      volkswagenCost: '',
    });
    setShowVehicleListModal(false);
    setShowChangeCustomerModal(false);
    setShowSearchCustomerModal(false);
    setShowAddCustomerModal(false);
    setSearchCompleted(false);
    setShowCampaignModal(false);
    setVehicleCampaigns([]);
  };

  const findMechanicConflict = useCallback(() => {
    if (!resolveMechanicId(newService.mechanicId)) return null;
    const requestedStart = parseAppointmentDateTime(
      newService.date,
      newService.time,
    );
    const requestedDuration = Number(newService.estimatedTime) || 30;
    if (!requestedStart) return null;
    const requestedEnd = new Date(
      requestedStart.getTime() + requestedDuration * 60 * 1000,
    );
    return (
      appointmentList.find((appointment) => {
        if (
          String(appointment.status || '').toLowerCase() === 'cancelado' ||
          Number(appointment.mechanicId) !== Number(newService.mechanicId)
        ) {
          return false;
        }
        const appointmentStart = parseAppointmentDateTime(
          appointment.date,
          appointment.time,
        );
        if (!appointmentStart) return false;
        const appointmentDuration =
          Number(appointment.estimatedTime) ||
          sumServiceEventsMinutes(appointment.service?.servicesEvents || []) ||
          30;
        const appointmentEnd = new Date(
          appointmentStart.getTime() + appointmentDuration * 60 * 1000,
        );
        return requestedStart < appointmentEnd && requestedEnd > appointmentStart;
      }) || null
    );
  }, [appointmentList, newService]);

  const saveAppointment = async ({ allowOverlap = false } = {}) => {
    const selectedService =
      newService.service ||
      servicesList.find((s) => s.id === parseInt(newService.serviceId, 10));
    const serviceTypeName =
      servicesTypeList.find(
        (t) => Number(t.id) === Number(newService.serviceTypeId),
      )?.name || '';
    const isWarranty = isWarrantyServiceType(
      newService.serviceTypeId,
      servicesTypeList,
    );
    const warrantyTypeCode = isWarranty
      ? newService.warrantyType || DEFAULT_WARRANTY_TYPE_CODE
      : null;
    const warrantyTypeLabel = warrantyTypeCode
      ? getWarrantyTypeLabel(warrantyTypeCode)
      : '';
    const serviceId = selectedService?.id
      ? Number(selectedService.id)
      : newService.serviceId
        ? Number(newService.serviceId)
        : null;

    if (!Number(newService.estimatedTime)) {
      toast({
        title: 'Duración en 0',
        description:
          'El turno se guardará con una duración por defecto de 30 min. Podés ajustarla luego desde la agenda.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }

    const service = {
      ...newService,
      estimatedTime: Number(newService.estimatedTime) || 30,
      customerTransferId: Number(newService.customerTransferId),
      parking: Number(newService.parking),
      mechanicId: resolveMechanicId(newService.mechanicId),
      serviceTypeId: Number(newService.serviceTypeId),
      serviceId,
      customerId: selectedCustomer.id,
      vehicleId: selectedVehicle.id,
      warrantyType: warrantyTypeCode,
      description: isWarranty
        ? `${serviceTypeName} · ${warrantyTypeLabel}`
        : selectedService?.name || newService.description || serviceTypeName,
      service: selectedService || null,
      status: 'Pendiente',
      createdAt: new Date(),
      updatedAt: new Date(),
      allowOverlap: Boolean(allowOverlap),
    };

    setIsSubmitting(true);
    try {
      const response = await postData('/agenda', service);
      await postNewService({
        ...response.payload.agenda,
        volkswagenCost: service.volkswagenCost,
        warrantyType: service.warrantyType,
        observations: service.observations,
        service: selectedService || null,
        estimatedTime: service.estimatedTime,
        description: service.description,
      });
      handleCancelService();
      setOverlapConfirm({ isOpen: false, message: '', allowOverlap: false });
      setNoMechanicConfirm(false);
      toast({
        title: 'Servicio agendado',
        description: 'El servicio se agendó correctamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/admin/default');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Error al agendar el servicio.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const proceedScheduleAfterValidation = async () => {
    const conflictingAppointment = findMechanicConflict();
    if (conflictingAppointment) {
      const conflictStart = parseAppointmentDateTime(
        conflictingAppointment.date,
        conflictingAppointment.time,
      );
      const conflictLabel = conflictStart
        ? conflictStart.toLocaleTimeString('es-UY', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        : 'ese horario';

      setOverlapConfirm({
        isOpen: true,
        allowOverlap: true,
        message: `El operario ya tiene un servicio agendado a las ${conflictLabel}. ¿Desea agendar igualmente en el horario indicado?`,
      });
      return;
    }

    await saveAppointment({ allowOverlap: false });
  };

  const handleSubmit = async () => {
    const isMaint = isMaintenanceServiceType(
      newService.serviceTypeId,
      servicesTypeList,
    );
    if (
      !newService.serviceTypeId ||
      !newService.customerTransferId ||
      !newService.parking ||
      !newService.date ||
      !newService.time
    ) {
      toast({
        title: 'Datos incompletos',
        description:
          'Complete tipo, traslado, estacionamiento, fecha y horario (operario opcional).',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (isMaint && !newService.serviceId) {
      toast({
        title: 'Servicio requerido',
        description: 'Seleccione un servicio de mantenimiento.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (!isMaint && !(Number(newService.estimatedTime) > 0)) {
      toast({
        title: 'Duración requerida',
        description: 'Ingrese la duración del turno (hh:mm, ej. 01:30).',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!resolveMechanicId(newService.mechanicId)) {
      setNoMechanicConfirm(true);
      return;
    }

    await proceedScheduleAfterValidation();
  };

  const handleVehicleCreated = (vehicle) => {
    handleSelectVehicle(vehicle);
  };

  const handleSubmitNewCustomer = async (customerData) => {
    try {
      const response = await postData('/customers', customerData);
      handleSelectCustomer(response.payload.customer);
      toast({
        title: 'Cliente creado',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo crear el cliente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <AdminPageShell fullHeight>
      <ServiceDetails
        newService={newService}
        handleServiceChange={handleServiceChange}
        handleSubmit={handleSubmit}
        selectedVehicle={selectedVehicle}
        selectedCustomer={selectedCustomer}
        mechanicsList={mechanicsList}
        serviceTypesList={servicesTypeList}
        servicesList={servicesList}
        customerTransferList={customerTransferList}
        handleCancelService={handleCancelService}
        appointmentList={appointmentList}
        selectedDate={selectedDate}
        showByDate={showByDate}
        handleToggleButtonDate={handleToggleButtonDate}
        setSelectedDate={setSelectedDate}
        holidayList={holidayList}
        onAppointmentRangeChange={handleAppointmentRangeChange}
        onOpenCampaigns={() => setShowCampaignModal(true)}
        vehicleInfo={vehicleInfo}
        setVehicleInfo={setVehicleInfo}
        onSearchVehicle={searchVehicle}
        isSearchingVehicle={isSearchingVehicles}
        handleChangeCustomer={handleChangeCustomer}
      />

      <VehicleListModal
        isOpen={showVehicleListModal}
        closeVehicleListModal={() => setShowVehicleListModal(false)}
        vehiclesList={vehiclesList}
        matchedCustomers={matchedCustomers}
        handleSelectVehicle={handleSelectVehicle}
        onVehicleCreated={handleVehicleCreated}
      />
      <CustomerChangeModal
        isOpen={showChangeCustomerModal}
        onClose={() => setShowChangeCustomerModal(false)}
        confirmCustomerChange={confirmCustomerChange}
      />
      <CustomerSearchModal
        isOpen={showSearchCustomerModal}
        closeSearchClientModal={() => setShowSearchCustomerModal(false)}
        handleCustomerSearch={handleCustomerSearch}
        customerSearch={customerSearch}
        setCustomerSearch={setCustomerSearch}
        isSearchingCustomer={false}
        customersList={customersList}
        handleSelectCustomer={handleSelectCustomer}
        handleAddCustomer={handleAddCustomer}
        searchCompleted={searchCompleted}
      />
      <AddCustomerModal
        isOpen={showAddCustomerModal}
        closeAddClientModal={() => setShowAddCustomerModal(false)}
        handleSubmitNewCustomer={handleSubmitNewCustomer}
        customerTypes={customerTypesList}
        localities={localitiesList}
      />
      <CampaignValidationModal
        isOpen={showCampaignModal}
        onClose={() => setShowCampaignModal(false)}
        campaigns={vehicleCampaigns}
        chassisNumber={selectedVehicle?.chassisNumber || ''}
      />
      <ConfirmModal
        isOpen={noMechanicConfirm}
        onClose={() => setNoMechanicConfirm(false)}
        onConfirm={async () => {
          setNoMechanicConfirm(false);
          await proceedScheduleAfterValidation();
        }}
        header="Sin operario asignado"
        message="No seleccionó un operario para este turno. ¿Desea agendar igualmente? Podrá asignarlo más adelante desde la agenda."
        confirmLabel="Agendar sin operario"
        cancelLabel="Volver"
        confirmVariant="warning"
        isLoading={isSubmitting}
      />
      <ConfirmModal
        isOpen={overlapConfirm.isOpen}
        onClose={() =>
          setOverlapConfirm({ isOpen: false, message: '', allowOverlap: false })
        }
        onConfirm={() => saveAppointment({ allowOverlap: true })}
        header="Operario ocupado"
        message={overlapConfirm.message}
        confirmLabel="Aceptar y agendar"
        cancelLabel="Cancelar"
        confirmVariant="warning"
        isLoading={isSubmitting}
      />
    </AdminPageShell>
  );
};

export default ServiceAppointmentForm;
